import { useTranslation } from "react-i18next";
import { Box, Grid } from "@mui/material";
import { PageLayout } from "@propai-web/lib/components/shared/PageLayout";
import { createFileRoute } from "@tanstack/react-router";

import {
  GraphWithControls,
  IndoorClimateChartContainer,
  IndoorClimateSignatureDiagramContainer,
  SensorOverview,
  useIndoorClimateGraphData,
  useIndoorClimateOverviewData,
  useIndoorClimateSensorSelection,
} from "@utilifeed/components/propai";
import { useFlexstationClimateSeries, useGetFlexStations } from "@core/apis/PropAI/hooks";
import { SolidCard } from "@components";

export const IndoorClimatePage = () => {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const { substationId } = Route.useParams();
  const { data: flexStations } = useGetFlexStations(substationId);
  const flexStationUid = flexStations?.[0]?.uid;

  const {
    resolution,
    setResolution,
    dateRange,
    setDateRange,
    visibleSeries,
    selectedIndividualSensors,
    selectedSensors,
    getDisplayOptions,
    handleSensorSelect,
    handleSensorOverviewSelect,
    handleSeriesToggle,
    handleDisplayOptionChange,
  } = useIndoorClimateSensorSelection();

  const { data: overviewData, isLoading: isOverviewLoading } = useIndoorClimateOverviewData(
    substationId,
    { flexStationUid }
  );
  const { individualSensors, fullSensorList, avgSensor, targetTemperature } = overviewData;

  const { data: graphData, isLoading: isGraphLoading } = useIndoorClimateGraphData(
    substationId,
    dateRange
  );
  const { sanitizedSensors, outdoorSeries, weatherCoordinates } = graphData;

  const { data: climateSeries } = useFlexstationClimateSeries(flexStationUid, dateRange);
  const flexstationTemperatureBounds = climateSeries?.map((point) => ({
    timestamp: point.timestamp,
    min: point.temperature_min,
    max: point.temperature_max,
  }));

  const displayOptions = getDisplayOptions(t);

  return (
    <PageLayout>
      <Grid item xs={12}>
        <SolidCard title={t("climate.indoor_climate_now")}>
          <SensorOverview
            selectedSensors={selectedIndividualSensors}
            setSelectedSensors={handleSensorOverviewSelect}
            sensors={individualSensors}
            fullSensors={fullSensorList}
            onSensorSelect={handleSensorSelect}
            avgSensor={avgSensor}
            isLoading={isOverviewLoading}
            targetTemperature={targetTemperature}
          />
        </SolidCard>
      </Grid>

      <Grid item xs={12}>
        <GraphWithControls
          title={t("climate.indoor_climate_graph_title")}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          resolution={resolution}
          onResolutionChange={setResolution}
          showControls
          showQuickDates
          showDataSettings
          displayOptions={displayOptions}
          onDisplayOptionsChange={handleDisplayOptionChange}
        >
          <Box width="100%" height={700}>
            <IndoorClimateChartContainer
              sensors={sanitizedSensors}
              selectedSensors={selectedSensors}
              outdoorSeries={outdoorSeries}
              flexstationTemperatureBounds={flexstationTemperatureBounds}
              visibleSeries={visibleSeries}
              onSeriesToggle={handleSeriesToggle}
              dateRange={dateRange}
              resolution={resolution}
              isLoading={isGraphLoading}
              chartHeight={680}
              targetTemperature={targetTemperature}
            />
          </Box>
          <Box>
            <IndoorClimateSignatureDiagramContainer
              sensors={sanitizedSensors}
              selectedSensors={selectedSensors}
              dateRange={dateRange}
              resolution={resolution}
              coordinates={weatherCoordinates}
            />
          </Box>
        </GraphWithControls>
      </Grid>
    </PageLayout>
  );
};

export const Route = createFileRoute("/$organizationId/properties/$substationId/indoor-climate")({
  component: IndoorClimatePage,
});
